/**
 * Node-related API helpers.
 * Mirrors the Synapse service structure for consistency.
 */
export const createNodeService = (apiClient) => {
  if (!apiClient) {
    throw new Error('apiClient instance is required to create Node service');
  }

  return {
    /**
     * Fetch nodes for a graph.
     * @param {Object} options
     * @param {string} options.graphId - Graph identifier (required)
     * @param {number} [options.page=1] - Page number
     * @param {number} [options.pageSize=25] - Records per page
     * @param {string} [options.query] - Search query
     * @returns {Promise<Object>} API response payload
     */
    listNodes: async ({ graphId, page = 1, pageSize = 25, query }) => {
      if (!graphId) {
        throw new Error('graphId is required to list nodes');
      }

      const params = {
        graphId,
        page,
        pageSize,
      };

      if (typeof query === 'string' && query.trim() !== '') {
        params.query = query.trim();
      }

      // Increase timeout for list operations which may take longer
      const response = await apiClient.get('/nodes', {
        params,
        timeout: 60000, // 60 seconds for list operations
      });
      console.log('response', response.data);
      return response.data;
    },

    /**
     * Retrieve a node by its ID.
     * @param {string} nodeId - Node identifier (required).
     * @param {Object} options - Optional parameters (e.g., { include_related: true }).
     * @returns {Promise<Object>} API response payload.
     */
    getNode: async (nodeId, options = {}) => {
      if (!nodeId) {
        throw new Error('nodeId is required to fetch node details');
      }

      const params = new URLSearchParams();
      if (options.include_related) {
        params.append('include_related', 'true');
      }

      const url = `/nodes/${nodeId}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get(url, { timeout: 30000 }); // 30s for detail page
      return response.data;
    },

    /**
     * Update node metadata (name, description, type, attributes).
     * @param {string} nodeId
     * @param {Object} update
     * @returns {Promise<Object>}
     */
    updateNode: async (nodeId, update) => {
      if (!nodeId) {
        throw new Error('nodeId is required to update node details');
      }
      const response = await apiClient.put(`/nodes/${nodeId}`, update);
      return response.data;
    },

    /**
     * Create a new node.
     * @param {Object} nodeData
     * @param {string} nodeData.graphId - Graph identifier (required)
     * @param {string} nodeData.name - Node name (required)
     * @param {string} [nodeData.type] - Node type
     * @param {string} [nodeData.description] - Description
     * @param {Array|Object} [nodeData.attributes] - Attributes
     * @param {string} [nodeData.retentionPolicyId] - Retention policy ID
     * @param {string} [nodeData.nodeKindId] - Node kind ID
     * @param {Object} [nodeData.related] - Related entities (fusionPolicy, rateCoding, settings)
     * @returns {Promise<Object>}
     */
    createNode: async (nodeData) => {
      if (!nodeData.graphId) {
        throw new Error('graphId is required to create node');
      }
      if (!nodeData.name) {
        throw new Error('name is required to create node');
      }
      const response = await apiClient.post('/nodes', nodeData);
      return response.data;
    },
  };
};

export default createNodeService;

